import { Header, Footer, PageTransition } from '../components';
import { SEOHead } from '../components/seo';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaPaperPlane, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { useSpring, animated, useTrail } from 'react-spring';
import { useState, useEffect, useRef } from 'react';
import heroImage from '../assets/contact_hero_bw.png';


function ContactForm() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.firstName || !formData.email || !formData.message) {
      setStatus('error');
      return;
    }

    setStatus('submitting');

    // Simulate API call
    setTimeout(() => {
      setStatus('success');
      setFormData({ firstName: '', lastName: '', email: '', message: '' });
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
    if (status === 'error') setStatus('idle');
  };

  if (status === 'success') {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center space-y-4 animate-fadeIn">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <FaCheckCircle className="text-green-500 text-3xl" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900">Message Sent!</h3>
        <p className="text-gray-600 max-w-xs">
          Thank you for contacting us. We will get back to you shortly.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-6 px-6 py-2 bg-gray-100 text-gray-600 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
          <input
            type="text"
            id="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg bg-gray-50 border-gray-200 focus:border-orange-500 focus:bg-white focus:ring-0 transition-colors"
            placeholder="John"
            required
          />
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
          <input
            type="text"
            id="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-lg bg-gray-50 border-gray-200 focus:border-orange-500 focus:bg-white focus:ring-0 transition-colors"
            placeholder="Doe"
          />
        </div>
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
        <input
          type="email"
          id="email"
          value={formData.email}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-lg bg-gray-50 border-gray-200 focus:border-orange-500 focus:bg-white focus:ring-0 transition-colors"
          placeholder="john@example.com"
          required
        />
      </div>
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
        <textarea
          id="message"
          rows={5}
          value={formData.message}
          onChange={handleChange}
          className="w-full px-4 py-3 rounded-lg bg-gray-50 border-gray-200 focus:border-orange-500 focus:bg-white focus:ring-0 transition-colors"
          placeholder="How can we help you?"
          required
        ></textarea>
      </div>

      {status === 'error' && (
        <div className="flex items-center text-red-500 text-sm bg-red-50 p-3 rounded-lg">
          <FaExclamationCircle className="mr-2" />
          Please fill in all required fields.
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className={`w-full bg-gray-900 text-white font-bold py-4 rounded-lg hover:bg-orange-500 transition-colors duration-300 flex items-center justify-center space-x-2 ${status === 'submitting' ? 'opacity-75 cursor-not-allowed' : ''}`}
      >
        {status === 'submitting' ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            <span>Sending...</span>
          </>
        ) : (
          <>
            <span>Send Message</span>
            <FaPaperPlane className="text-sm" />
          </>
        )}
      </button>
    </form>
  );
}

function ContactPage() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [contactCardsVisible, setContactCardsVisible] = useState(false);
  const [messageFormVisible, setMessageFormVisible] = useState(false);

  const heroRef = useRef<HTMLDivElement>(null);
  const contactCardsRef = useRef<HTMLDivElement>(null);
  const messageFormRef = useRef<HTMLDivElement>(null);

  const contactCards = [
    { icon: FaEnvelope, title: "Email Us", description: "Our friendly team is here to help.", link: "info@easybuytrd.com", href: "mailto:info@easybuytrd.com" },
    { icon: FaPhone, title: "Call Us", description: "Mon-Fri from 8am to 5pm." },
    { icon: FaMapMarkerAlt, title: "Visit Us", description: "Office – M Floor, WH-06, Al Qusais Ind 04" },
  ];

  // Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target;
            if (target === heroRef.current) setHeroVisible(true);
            if (target === contactCardsRef.current) setContactCardsVisible(true);
            if (target === messageFormRef.current) setMessageFormVisible(true);
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const refs = [heroRef, contactCardsRef, messageFormRef];
    refs.forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  // Hero animations
  const heroTextAnimation = useSpring({
    opacity: heroVisible ? 1 : 0,
    transform: heroVisible ? 'translateX(0px)' : 'translateX(-50px)',
    config: { mass: 1, tension: 120, friction: 30 },
  });

  const heroImageAnimation = useSpring({
    opacity: heroVisible ? 1 : 0,
    transform: heroVisible ? 'scale(1)' : 'scale(1.1)',
    config: { mass: 1, tension: 120, friction: 30 },
  });

  // Contact cards staggered animation
  const cardsTrail = useTrail(contactCards.length, {
    opacity: contactCardsVisible ? 1 : 0,
    transform: contactCardsVisible ? 'translateY(0px)' : 'translateY(40px)',
    config: { mass: 1, tension: 180, friction: 40 },
  });

  // Message form animations
  const messageHeaderAnimation = useSpring({
    opacity: messageFormVisible ? 1 : 0,
    transform: messageFormVisible ? 'translateY(0px)' : 'translateY(30px)',
    config: { mass: 1, tension: 120, friction: 30 },
  });

  const infoCardAnimation = useSpring({
    opacity: messageFormVisible ? 1 : 0,
    transform: messageFormVisible ? 'translateX(0px)' : 'translateX(-40px)',
    config: { mass: 1, tension: 120, friction: 30 },
    delay: 150,
  });

  const formAnimation = useSpring({
    opacity: messageFormVisible ? 1 : 0,
    transform: messageFormVisible ? 'translateX(0px)' : 'translateX(40px)',
    config: { mass: 1, tension: 120, friction: 30 },
    delay: 300,
  });

  return (
    <div className="min-h-screen bg-white">
      <SEOHead />
      <Header />
      <PageTransition>
        <main className="pt-16">
          {/* Split Header */}
          <div className="flex flex-col md:flex-row min-h-[500px] md:h-[70vh]" ref={heroRef}>
            <animated.div style={heroTextAnimation} className="w-full md:w-1/2 bg-gray-900 text-white flex flex-col justify-center px-6 py-16 md:px-20 md:py-0 relative overflow-hidden">
              <div className="absolute top-10 left-10 w-20 h-20 border-t-2 border-l-2 border-orange-500/30 hidden md:block"></div>
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-none mb-6">
                Get in <br /><span className="text-orange-500">Touch</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-400 font-light max-w-md border-l-2 border-orange-500 pl-4 md:pl-6">
                We're here to help and answer any question you might have. We look forward to hearing from you.
              </p>
            </animated.div>
            <animated.div style={heroImageAnimation} className="w-full md:w-1/2 relative bg-gray-800 min-h-[300px] md:min-h-0">
              <div className="absolute inset-0 bg-gray-900/40 mix-blend-multiply z-10"></div>
              <img
                src={heroImage}
                alt="Global Connectivity"
                className="w-full h-full object-cover grayscale contrast-125"
              />
            </animated.div>
          </div>

          {/* Contact Grid */}
          <div className="bg-gray-50 py-12 md:py-24 px-4 sm:px-6 lg:px-8 md:-mt-20 relative z-20" ref={contactCardsRef}>
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {/* Email Card */}
                {cardsTrail.map((style, idx) => (
                  <animated.div key={idx} style={style} className="bg-white p-6 md:p-10 shadow-lg rounded-xl border border-gray-100 hover:shadow-xl transition-shadow group">
                    {idx === 0 && (
                      <>
                        <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300">
                          <FaEnvelope className="text-orange-500 text-2xl group-hover:text-white transition-colors" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
                        <p className="text-gray-500 mb-4">Our friendly team is here to help.</p>
                        <a href="mailto:info@easybuytrd.com" className="text-lg font-semibold text-orange-600 hover:text-orange-700">info@easybuytrd.com</a>
                      </>
                    )}
                    {idx === 1 && (
                      <>
                        <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300">
                          <FaPhone className="text-orange-500 text-2xl group-hover:text-white transition-colors" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us</h3>
                        <p className="text-gray-500 mb-4">Mon-Fri from 8am to 5pm.</p>
                        <div className="flex flex-col space-y-1">
                          <a href="tel:+97143991663" className="text-lg font-semibold text-orange-600 hover:text-orange-700">+971 4 399 1663</a>
                          <a href="tel:+971582221672" className="text-lg font-semibold text-gray-700 hover:text-orange-700">+971 58 222 1672</a>
                        </div>
                      </>
                    )}
                    {idx === 2 && (
                      <>
                        <div className="w-16 h-16 bg-orange-50 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300">
                          <FaMapMarkerAlt className="text-orange-500 text-2xl group-hover:text-white transition-colors" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Visit Us</h3>
                        <p className="text-gray-500 mb-4">Office – M Floor, WH-06, Al Qusais Ind 04</p>
                        <p className="text-lg font-semibold text-gray-900">Dubai, UAE, PO Box 83487</p>
                      </>
                    )}
                  </animated.div>
                ))}
              </div>
            </div>
          </div>

          {/* Message Section */}
          <div className="bg-white py-12 md:py-24 px-4 sm:px-6 lg:px-8" ref={messageFormRef}>
            <div className="max-w-7xl mx-auto">
              {/* Header Text */}
              <animated.div style={messageHeaderAnimation} className="mb-12 md:mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 md:mb-6">Send us a message</h2>
                <p className="text-lg md:text-xl text-gray-600 max-w-3xl leading-relaxed">
                  Whether you have a question about our services, pricing, or anything else, our team is ready to answer all your questions.
                </p>
              </animated.div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-start">
                {/* Left Column: Info Card */}
                <animated.div style={infoCardAnimation} className="bg-gray-900 text-white p-6 md:p-8 rounded-2xl relative overflow-hidden space-y-6 md:space-y-8 h-full">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500 rounded-bl-full opacity-20"></div>

                  {/* Business Hours */}
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
                      <span>Business Hours</span>
                    </h3>
                    <div className="space-y-2 text-sm md:text-base text-gray-300">
                      <div className="flex justify-between gap-4">
                        <span>Monday - Friday</span>
                        <span className="font-semibold text-white">8:00 AM - 5:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Saturday</span>
                        <span className="font-semibold text-white">9:00 AM - 2:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sunday</span>
                        <span className="font-semibold text-white">Closed</span>
                      </div>
                    </div>
                  </div>

                  <hr className="border-gray-700" />

                  {/* Regional Contacts */}
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Regional Contacts</h3>
                    <div className="space-y-4 md:space-y-6">
                      <div>
                        <h4 className="font-bold text-orange-500 mb-1 text-sm md:text-base">Ethiopia</h4>
                        <p className="text-white text-sm md:text-base">+251 711 304 049</p>
                      </div>
                      <div>
                        <h4 className="font-bold text-orange-500 mb-1 text-sm md:text-base">Kenya & Tanzania</h4>
                        <p className="text-white text-sm md:text-base">+254 722 540 617</p>
                      </div>
                      <div>
                        <h4 className="font-bold text-orange-500 mb-1 text-sm md:text-base">Uganda</h4>
                        <p className="text-white text-sm md:text-base">+256 754 740 123 | +256 706 576 361</p>
                      </div>
                      <div>
                        <h4 className="font-bold text-orange-500 mb-1 text-sm md:text-base">Djibouti</h4>
                        <p className="text-white text-sm md:text-base">+253 77367011</p>
                      </div>
                    </div>
                  </div>
                </animated.div>

                {/* Right Column: Contact Form */}
                <animated.div style={formAnimation} className="bg-white p-8 md:p-10 rounded-2xl shadow-xl border border-gray-100 h-full">
                  {messageFormVisible && (
                    <ContactForm />
                  )}
                </animated.div>
              </div>
            </div>
          </div>

        </main>
      </PageTransition>
      <Footer />
    </div>
  );
}

export default ContactPage;